import type { FC, ReactNode } from 'react'

import { useMemo } from 'react'
import { ArrowUp } from 'lucide-react'
import { createWordmarkField } from '@/lib/dots/fields'
import { DotCanvas } from '@/components/dots/dot-canvas'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { NAV_LINKS } from '@/constants/content'
import { siteConfig } from '@/constants/site'

import {
    WORDMARK_PATHS,
    WORDMARK_STROKE,
    WORDMARK_VIEWBOX
} from '@/constants/brand'

const LABEL = 'font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground'

const LINK = 'text-[15px] text-ink transition-colors hover:text-azure-600'

export const SiteFooter: FC = (): ReactNode => {
    const field = useMemo(() => createWordmarkField(WORDMARK_PATHS, WORDMARK_VIEWBOX, WORDMARK_STROKE), [])
    const { contact } = siteConfig

    return (
        <footer className='border-t border-line bg-white'>
            <Container className='pt-16 md:pt-20'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <Logo />

                        <p className='mt-6 max-w-xs text-[15px] leading-relaxed text-muted-foreground'>
                            Independent software studio designing and engineering digital products since 2016.
                        </p>
                    </div>

                    <nav aria-label="Footer" className='lg:col-span-2 lg:col-start-6'>
                        <p className={LABEL}>Sitemap</p>

                        <ul className='mt-5 flex flex-col gap-3'>
                            {NAV_LINKS.map(link => (
                                <li key={link.href}>
                                    <a href={link.href} className={LINK}>
                                        {link.label}
                                    </a>
                                </li>
                            ))}

                            <li>
                                <a href='/contact' className={LINK}>
                                    Contact
                                </a>
                            </li>
                        </ul>
                    </nav>

                    <div className='lg:col-span-3'>
                        <p className={LABEL}>Studio</p>

                        <address className='mt-5 text-[15px] leading-relaxed text-ink not-italic'>
                            {contact.street}
                            <br />
                            {contact.city}
                        </address>
                    </div>

                    <div className='lg:col-span-3'>
                        <p className={LABEL}>Say hello</p>

                        <ul className='mt-5 flex flex-col gap-3'>
                            <li>
                                <a href={`mailto:${contact.email}`} className={LINK}>
                                    {contact.email}
                                </a>
                            </li>

                            <li>
                                <a href={`tel:${contact.phoneHref}`} className={LINK}>
                                    {contact.phone}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div aria-hidden='true' className='relative mt-16 aspect-[3/1] w-full md:mt-20'>
                    <DotCanvas
                        field={field}
                        grid
                        interactive
                        cell={8}
                        mobileCell={4}
                        startTime={2}
                    />
                </div>

                <div className='flex flex-col gap-3 border-t border-line py-6 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground md:flex-row md:items-center md:justify-between'>
                    <p>© 2026 {siteConfig.legalName}</p>
                    <p>Designed and built in Milano</p>

                    <a href='/#top' className='inline-flex items-center gap-2 transition-colors hover:text-ink'>
                        Back to top
                        <ArrowUp aria-hidden='true' className='size-3.5' />
                    </a>
                </div>
            </Container>
        </footer>
    )
}
